import { Injectable } from '@angular/core';
import { Auth, signInWithCustomToken, signOut } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject } from 'rxjs';

export interface ImpersonationState {
  active: boolean;
  tenantId?: string;
  tenantName?: string;
  adminEmail?: string;
}

const STORAGE_KEY = 'impersonation_state';

@Injectable({
  providedIn: 'root',
})
export class ImpersonationService {
  private stateSubject = new BehaviorSubject<ImpersonationState>(
    this.loadFromStorage(),
  );

  readonly state$ = this.stateSubject.asObservable();

  get isActive(): boolean {
    return this.stateSubject.value.active;
  }

  get currentState(): ImpersonationState {
    return this.stateSubject.value;
  }

  constructor(
    private readonly auth: Auth,
    private readonly functions: Functions,
  ) {}

  /**
   * Start impersonation: calls the Cloud Function to get a custom token
   * for the tenant's admin, signs in with it, and navigates to /dashboard.
   */
  async startImpersonation(tenantId: string): Promise<void> {
    const fn = httpsCallable<
      { tenantId: string },
      {
        customToken: string;
        tenantName: string;
        adminEmail: string;
        adminUid: string;
      }
    >(this.functions, 'getImpersonationToken');

    const { data } = await fn({ tenantId });

    // Sign in as the tenant admin
    await signInWithCustomToken(this.auth, data.customToken);

    const state: ImpersonationState = {
      active: true,
      tenantId,
      tenantName: data.tenantName,
      adminEmail: data.adminEmail,
    };

    this.saveToStorage(state);
    this.stateSubject.next(state);

    // Full page reload ensures all services re-initialize with the new auth context,
    // avoiding race conditions between signInWithCustomToken and onAuthStateChanged.
    window.location.assign('/dashboard');
  }

  /**
   * End impersonation: signs out the impersonated user and redirects to
   * the super-admin login page so the super-admin can re-authenticate.
   */
  async stopImpersonation(): Promise<void> {
    this.clearStorage();
    this.stateSubject.next({ active: false });
    await signOut(this.auth);
    window.location.assign('/login');
  }

  private loadFromStorage(): ImpersonationState {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw) as ImpersonationState;
      }
    } catch {
      // Ignore parse errors
    }
    return { active: false };
  }

  private saveToStorage(state: ImpersonationState): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  private clearStorage(): void {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}
